"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, useState } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { Accordion } from "@repo/ui/mantine";
import { Minus, Plus } from "lucide-react";
import { useAtom } from "jotai";
import { cn } from "@repo/ui/lib/utils";
import { useIsMobile } from "@repo/ui/hooks/use-mobile";
import { learningFormatAtom, selectedCampusAtom } from "@/app/atoms/courseLevelSelections";
import sectionStyles from "@/app/components/CourseLevelSection/CourseLevelSection.module.css";
import { CourseTag } from "@/app/components/CourseLevelPage/CourseTag/CourseTag";
import { SelectableTile } from "../SelectableTile";
import styles from "./LearningFormat.module.css";
import { LEARN_MORE_VALUE, FALLBACK_SUBTITLE, FALLBACK_TITLE, LEARN_MORE_KEY, FALLBACK_LEARN_MORE_TITLE, WHY_CHOOSE_KEY, FALLBACK_WHY_CHOOSE_TITLE, HIDE_DETAILS_KEY, FALLBACK_HIDE_DETAILS_TEXT, LearningFormatBestFor, LearningFormatDetailRow, LearningFormatHeader, LearningFormatQuickFact, LearningFormatWhyChoose, StickyFormatChrome, isBeastAcademyModality, useModalityRows, type LearningFormatProps, type ModalityRow, STEP_TAG_KEY, FALLBACK_STEP_TAG } from "./LearningFormatParts";
import type { CoursePageSection } from "@repo/sanity-utils/query-types";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { didOpenLearnMore } from "../LearningFormatSelectionUtils";
type MobileOptionProps = {
  row: ModalityRow;
  courseSlug: string;
  onSelect: (modality: ModalityRow["modality"]) => void;
  coursePageSection: CoursePageSection;
  linkToBAO: () => void;
};
function MobileFormatOption(t0) {
  const $ = _c(53);
  const {
    row,
    courseSlug,
    onSelect,
    coursePageSection,
    linkToBAO
  } = t0;
  const {
    modality,
    modalityOffering,
    priceLines,
    quickFacts,
    isSelectDisabled,
    isSelected
  } = row;
  const [openedAccordion, setOpenedAccordion] = useState(null);
  const isLearnMoreOpen = openedAccordion === LEARN_MORE_VALUE;
  const isPhone = useIsMobile() === true;
  const hasWhyChoose = (modality.whyChoose?.length ?? 0) > 0;
  const hasBestFor = (modality.bestFor?.length ?? 0) > 0;
  const hasDetails = hasWhyChoose || hasBestFor || quickFacts.length > 0;
  let keyedCopy;
  let learnMoreText;
  let t1;
  if ($[0] !== coursePageSection?.keyedCopy || $[1] !== modality.title) {
    keyedCopy = coursePageSection?.keyedCopy ?? [];
    learnMoreText = getKeyedCopyText(keyedCopy ?? [], LEARN_MORE_KEY) || FALLBACK_LEARN_MORE_TITLE;
    t1 = (getKeyedCopyText(keyedCopy ?? [], WHY_CHOOSE_KEY) || FALLBACK_WHY_CHOOSE_TITLE).replace("{{modality.title}}", modality.title);
    $[0] = coursePageSection?.keyedCopy;
    $[1] = modality.title;
    $[2] = keyedCopy;
    $[3] = learnMoreText;
    $[4] = t1;
  } else {
    keyedCopy = $[2];
    learnMoreText = $[3];
    t1 = $[4];
  }
  const whyChooseText = t1;
  let t2;
  if ($[5] !== keyedCopy) {
    t2 = getKeyedCopyText(keyedCopy ?? [], HIDE_DETAILS_KEY) || FALLBACK_HIDE_DETAILS_TEXT;
    $[5] = keyedCopy;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const hideDetailsText = t2;
  const collapsedLabel = isPhone && modality.title ? whyChooseText : learnMoreText;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(sectionStyles.optionCard, styles.mobileOptionCard);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isSelectDisabled || undefined;
  let t5;
  if ($[8] !== courseSlug || $[9] !== isSelectDisabled || $[10] !== isSelected || $[11] !== linkToBAO || $[12] !== modality || $[13] !== modalityOffering || $[14] !== onSelect || $[15] !== priceLines) {
    t5 = <StickyFormatChrome>{isStuck => <><SelectableTile.Content><LearningFormatHeader modality={modality} modalityOffering={modalityOffering} priceLines={priceLines} isCompact={isStuck} /></SelectableTile.Content><SelectableTile.SelectButton isSelected={isSelected} disabled={isSelectDisabled} onClick={() => {
          sendDiscoveryEventBeacon("course_modality_selected", {
            course_level: courseSlug,
            modality: modality.slug.current,
            auto_select: false
          });
          if (isBeastAcademyModality(modality)) {
            linkToBAO();
          }
          onSelect(modality);
        }} /></>}</StickyFormatChrome>;
    $[8] = courseSlug;
    $[9] = isSelectDisabled;
    $[10] = isSelected;
    $[11] = linkToBAO;
    $[12] = modality;
    $[13] = modalityOffering;
    $[14] = onSelect;
    $[15] = priceLines;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== hasWhyChoose || $[18] !== isLearnMoreOpen || $[19] !== keyedCopy || $[20] !== modality) {
    t6 = hasWhyChoose && <><SelectableTile.Content className={cn(styles.whyChooseTablet, isLearnMoreOpen && styles.formatWhyChoose)}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</SelectableTile.Content><div className={styles.whyChoosePhone}><LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</LearningFormatDetailRow></div></>;
    $[17] = hasWhyChoose;
    $[18] = isLearnMoreOpen;
    $[19] = keyedCopy;
    $[20] = modality;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== hasBestFor || $[23] !== isLearnMoreOpen || $[24] !== keyedCopy || $[25] !== modality) {
    t7 = hasBestFor && <LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatBestFor modality={modality} keyedCopy={keyedCopy} /></LearningFormatDetailRow>;
    $[22] = hasBestFor;
    $[23] = isLearnMoreOpen;
    $[24] = keyedCopy;
    $[25] = modality;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== isLearnMoreOpen || $[28] !== quickFacts) {
    let t9;
    if ($[30] !== isLearnMoreOpen) {
      t9 = fact => <LearningFormatDetailRow key={fact._key} expanded={isLearnMoreOpen}><LearningFormatQuickFact fact={fact} /></LearningFormatDetailRow>;
      $[30] = isLearnMoreOpen;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    t8 = quickFacts.map(t9);
    $[27] = isLearnMoreOpen;
    $[28] = quickFacts;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[32] !== isSelectDisabled || $[33] !== isSelected || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <div className={sectionStyles.optionCardDescription}><SelectableTile isSelected={isSelected} disabled={isSelectDisabled}>{t5}{t6}{t7}{t8}</SelectableTile></div>;
    $[32] = isSelectDisabled;
    $[33] = isSelected;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== t4 || $[40] !== t9) {
    t10 = <div className={styles.mobileCardFrame}><div className={t3} data-disabled={t4}>{t9}</div></div>;
    $[39] = t4;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== collapsedLabel || $[43] !== courseSlug || $[44] !== hasDetails || $[45] !== hideDetailsText || $[46] !== isLearnMoreOpen || $[47] !== modality.slug.current || $[48] !== openedAccordion) {
    t11 = hasDetails && <Accordion variant="unstyled" value={openedAccordion} onChange={value => {
      if (didOpenLearnMore(value, openedAccordion, LEARN_MORE_VALUE)) {
        sendDiscoveryEventBeacon("course_modality_details_viewed", {
          course_level: courseSlug,
          modality: modality.slug.current
        });
      }
      setOpenedAccordion(value);
    }} chevron={null} classNames={{
      root: styles.mobileAccordionRoot,
      item: styles.mobileAccordionItem,
      control: styles.mobileAccordionControl,
      label: styles.accordionLabel
    }}><Accordion.Item value={LEARN_MORE_VALUE}><Accordion.Control onClick={_temp}><span className={styles.accordionControlInner}><Typography variant="buttonMd" as="span">{isLearnMoreOpen ? hideDetailsText : collapsedLabel}</Typography>{isLearnMoreOpen ? <Minus size={24} strokeWidth={1.5} aria-hidden={true} /> : <Plus size={24} strokeWidth={1.5} aria-hidden={true} />}</span></Accordion.Control></Accordion.Item></Accordion>;
    $[42] = collapsedLabel;
    $[43] = courseSlug;
    $[44] = hasDetails;
    $[45] = hideDetailsText;
    $[46] = isLearnMoreOpen;
    $[47] = modality.slug.current;
    $[48] = openedAccordion;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t10 || $[51] !== t11) {
    t12 = <div className={styles.mobileOption}>{t10}{t11}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  return t12;
}

/**
 * Single-column Learning Format for tablet/phone (&lt;1024px). Each option owns
 * its own Learn more accordion. On phone (&lt;768px), whyChoose moves into the
 * detail rows via Collapse (see `.whyChooseTablet` / `.whyChoosePhone` CSS).
 */
function _temp(e) {
  e.stopPropagation();
}
export function LearningFormatMobile(t0) {
  const $ = _c(37);
  const {
    campuses,
    campusCoursePrices,
    courseLevel,
    coursePageSection,
    campusLocationCoursePageSection,
    linkToBAO,
    onSelectLearningFormat
  } = t0;
  const titleId = useId();
  const [selectedLearningFormat] = useAtom(learningFormatAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  let t1;
  if ($[0] !== coursePageSection?.keyedCopy) {
    t1 = coursePageSection?.keyedCopy ?? [];
    $[0] = coursePageSection?.keyedCopy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const modalityRows = useModalityRows(courseLevel, selectedLearningFormat, campuses, selectedCampusId, campusCoursePrices, t1, campusLocationCoursePageSection);
  let t2;
  if ($[2] !== coursePageSection?.title) {
    t2 = coursePageSection?.title?.trim() || FALLBACK_TITLE;
    $[2] = coursePageSection?.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const title = t2;
  let t3;
  if ($[4] !== coursePageSection?.body) {
    t3 = coursePageSection?.body?.trim() || FALLBACK_SUBTITLE;
    $[4] = coursePageSection?.body;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const subtitle = t3;
  let t4;
  if ($[6] !== coursePageSection?.keyedCopy) {
    t4 = getKeyedCopyText(coursePageSection?.keyedCopy ?? [], STEP_TAG_KEY) || FALLBACK_STEP_TAG;
    $[6] = coursePageSection?.keyedCopy;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const stepTag = t4;
  if (!modalityRows.length) {
    return null;
  }
  let t5;
  if ($[8] !== stepTag) {
    t5 = <CourseTag>{stepTag}</CourseTag>;
    $[8] = stepTag;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== title || $[11] !== titleId) {
    t6 = <Typography id={titleId} as="h2" variant="h4">{title}</Typography>;
    $[10] = title;
    $[11] = titleId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== subtitle) {
    t7 = <Typography variant="body">{subtitle}</Typography>;
    $[13] = subtitle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={sectionStyles.header}>{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== courseLevel?.slug || $[20] !== coursePageSection || $[21] !== linkToBAO || $[22] !== modalityRows || $[23] !== onSelectLearningFormat) {
    let t10;
    if ($[25] !== courseLevel?.slug || $[26] !== coursePageSection || $[27] !== linkToBAO || $[28] !== onSelectLearningFormat) {
      t10 = row => <MobileFormatOption key={row.modality._id} row={row} courseSlug={courseLevel?.slug.current ?? ""} onSelect={onSelectLearningFormat} coursePageSection={coursePageSection ?? null} linkToBAO={linkToBAO} />;
      $[25] = courseLevel?.slug;
      $[26] = coursePageSection;
      $[27] = linkToBAO;
      $[28] = onSelectLearningFormat;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    t9 = modalityRows.map(t10);
    $[19] = courseLevel?.slug;
    $[20] = coursePageSection;
    $[21] = linkToBAO;
    $[22] = modalityRows;
    $[23] = onSelectLearningFormat;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[30] !== t9) {
    t10 = <div className={styles.mobileBody}>{t9}</div>;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8 || $[34] !== title || $[35] !== titleId) {
    t11 = <section id="formats" className={sectionStyles.section} aria-label={title} aria-labelledby={titleId}>{t8}{t10}</section>;
    $[32] = t10;
    $[33] = t8;
    $[34] = title;
    $[35] = titleId;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}